/**
 * InvalidBindingCard — render-time error placeholder when a widget's
 * required slot is bound to keys with incompatible DataKind.
 *
 * Drawn by the registry's render guard (in DashboardRenderer) so silent
 * NaNs never reach Recharts.
 */

import { AlertTriangle, Settings } from "lucide-react";
import { DATA_KIND_LABEL, type DataKind } from "./data-kinds";

export interface InvalidBindingProblem {
  /** The slot whose binding is wrong. */
  slotLabel: string;
  /** The offending key. */
  key: string;
  /** What kind the key actually emits. */
  actualKind: DataKind;
  /** Kinds the slot would accept. */
  allowedKinds: DataKind[];
}

export interface InvalidBindingCardProps {
  widgetName: string;
  problems: InvalidBindingProblem[];
  onOpenPanel?: () => void;
}

export function InvalidBindingCard({
  widgetName,
  problems,
  onOpenPanel,
}: InvalidBindingCardProps) {
  return (
    <div className="flex h-full flex-col gap-2 rounded-md border border-danger/40 bg-danger/5 p-3">
      <div className="flex items-center gap-1.5 text-xs font-semibold text-danger">
        <AlertTriangle className="size-3.5" />
        <span>{widgetName} — invalid binding</span>
      </div>

      <ul className="flex flex-col gap-1 text-[11px] text-on-surface">
        {problems.map((p, i) => (
          <li key={i} className="rounded border border-danger/20 bg-surface px-2 py-1">
            <div className="flex items-baseline justify-between gap-2">
              <span className="font-mono text-on-surface">{p.key}</span>
              <span className="rounded-full bg-danger/15 px-1.5 py-0.5 text-[9px] font-medium uppercase tracking-wide text-danger">
                {DATA_KIND_LABEL[p.actualKind]}
              </span>
            </div>
            <p className="mt-0.5 text-[10px] text-on-surface-muted">
              <strong>{p.slotLabel}</strong> needs{" "}
              {p.allowedKinds.map((k) => DATA_KIND_LABEL[k]).join(" / ")}.
            </p>
          </li>
        ))}
      </ul>

      {onOpenPanel && (
        <button
          type="button"
          onClick={onOpenPanel}
          className="mt-auto inline-flex items-center justify-center gap-1.5 self-start rounded-md border border-danger/40 bg-surface px-2 py-1 text-[11px] font-medium text-danger hover:bg-danger/10"
        >
          <Settings className="size-3" />
          Fix in data panel
        </button>
      )}
    </div>
  );
}
